import { useMemo } from 'react';
import { Popover } from '../primitives';
import type { PropertyChoice } from './types';

export interface PropertiesPickerProps {
  mode: 'columns' | 'chips';
  /** All choices available in this mode, in canonical order. */
  choices: ReadonlyArray<PropertyChoice>;
  /** Keys that are always-on (rendered checked + disabled). */
  lockedKeys: ReadonlyArray<string>;
  /** Currently visible keys (subset of choices). */
  visible: ReadonlyArray<string>;
  onVisibleChange: (next: ReadonlyArray<string>) => void;
  /** Test-id prefix so each host page gets stable selectors. */
  testIdPrefix: string;
}

/**
 * `Properties ▾` — opens a popover with one checkbox per available property.
 * Locked keys (e.g. Title) render checked + disabled. Toggling preserves the
 * canonical choice order so the resulting `?cols=` string is stable.
 *
 * Mode is `columns` on a Table tab (controls spreadsheet columns) and `chips`
 * on a Board/Gallery tab (controls card-level metadata). The same `?cols=` URL
 * state round-trips both modes within a page.
 */
export function PropertiesPicker({
  mode,
  choices,
  lockedKeys,
  visible,
  onVisibleChange,
  testIdPrefix
}: PropertiesPickerProps) {
  const orderedKeys = useMemo(() => choices.map((c) => c.key), [choices]);

  function toggle(key: string) {
    if (lockedKeys.includes(key)) return;
    const has = visible.includes(key);
    const next = has
      ? visible.filter((k) => k !== key)
      : orderedKeys.filter((k) => visible.includes(k) || k === key);
    onVisibleChange(next);
  }

  return (
    <Popover
      trigger={(api) => (
        <button
          type="button"
          className="pn-btn pn-btn-sm"
          data-testid={`${testIdPrefix}-properties-trigger`}
          data-mode={mode}
          {...api}
        >
          Properties ▾
        </button>
      )}
      label="Properties"
      align="end"
      data-testid={`${testIdPrefix}-properties-popover`}
    >
      {() => (
        <div className="pn-listctl-properties-list">
          {choices.map((c) => {
            const locked = lockedKeys.includes(c.key);
            const checked = locked || visible.includes(c.key);
            return (
              <label
                key={c.key}
                className="pn-listctl-properties-item"
                data-testid={`${testIdPrefix}-properties-item-${c.key}`}
                data-locked={locked ? '1' : '0'}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={locked}
                  onChange={() => toggle(c.key)}
                  data-testid={`${testIdPrefix}-properties-toggle-${c.key}`}
                />
                <span>{c.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </Popover>
  );
}
